import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import Hero from './Hero';
import { Cloud, TrendingUp, LightbulbOutlined, GroupsOutlined, RocketLaunchOutlined, AutoAwesomeOutlined } from '@mui/icons-material';
import EligibilityChecklist from './EligibilityChecklist';
import AssessmentLinks from './AssessmentLinks';

type Page = 'home' | 'eligibility' | 'assessments';
type Track = 'cloud' | 'data' | null;

interface HomePageProps {
  currentPage: Page;
  selectedTrack: Track;
  setCurrentPage: (page: Page) => void;
  setSelectedTrack: (track: Track) => void;
}

const HomePage: React.FC<HomePageProps> = ({
  currentPage,
  selectedTrack,
  setCurrentPage,
  setSelectedTrack
}) => {
  const handleTrackClick = (track: 'cloud' | 'data') => {
    setSelectedTrack(track);
    setCurrentPage('eligibility');
  };

  const handleEligibilityComplete = () => {
    setCurrentPage('assessments');
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
    setSelectedTrack(null);
  };

  // Show eligibility checklist
  if (currentPage === 'eligibility' && selectedTrack) {
    return (
      <EligibilityChecklist
        track={selectedTrack}
        onComplete={handleEligibilityComplete}
        onBack={handleBackToHome}
      />
    );
  }

  // Show assessment links
  if (currentPage === 'assessments' && selectedTrack) {
    return (
      <AssessmentLinks
        track={selectedTrack}
        onBack={handleBackToHome}
      />
    );
  }

  // Show home page with track selection
  return (
    <Box sx={{
      maxWidth: 1200,
      mx: 'auto',
      px: 2,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'calc(100vh - 240px)',
    }}>
      <Hero />

      {/* Choose Your Track Section */}
      <Box sx={{
        mt: 0.5,
        mb: 1,
        py: 1,
      }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          align="center"
          sx={{
            mb: 0.5,
            fontWeight: 700,
            background: 'linear-gradient(90deg, #67e8f9 0%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Choose Your Track
        </Typography>

        <Typography
          variant="body2"
          align="center"
          sx={{
            mb: 2.5,
            fontFamily: '"Fira Code", "JetBrains Mono", monospace',
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          <Box component="span" sx={{ color: 'rgba(148,163,184,0.6)' }}>{'>'}</Box> select_specialization()
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 4,
            justifyContent: 'center',
            flexWrap: 'wrap',
            px: 2
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<Cloud />}
            onClick={() => handleTrackClick('cloud')}
            sx={{
              minWidth: 300,
              py: 2.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 5,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
              color: '#0a0a0a',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '2px solid rgba(245,158,11,0.5)',
              boxShadow: '0 4px 20px rgba(245,158,11,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
              fontFamily: '"Inter", sans-serif',
              letterSpacing: '0.5px',
              '&:hover': {
                transform: 'translateY(-6px) scale(1.02)',
                boxShadow: '0 12px 40px rgba(245,158,11,0.6), 0 0 0 2px rgba(251,191,36,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
                border: '2px solid rgba(251,191,36,0.8)',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
              },
              '&::before': {
                content: '"// Cloud Infrastructure"',
                position: 'absolute',
                top: 8,
                left: 12,
                fontSize: '9px',
                fontFamily: '"Fira Code", monospace',
                color: 'rgba(0,0,0,0.3)',
                letterSpacing: '0.5px',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover::after': {
                left: '100%',
              }
            }}
          >
            Cloud Engineer
          </Button>

          <Button
            variant="contained"
            size="large"
            startIcon={<TrendingUp />}
            onClick={() => handleTrackClick('data')}
            sx={{
              minWidth: 300,
              py: 2.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 5,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
              color: '#0a0a0a',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '2px solid rgba(245,158,11,0.5)',
              boxShadow: '0 4px 20px rgba(245,158,11,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
              fontFamily: '"Inter", sans-serif',
              letterSpacing: '0.5px',
              '&:hover': {
                transform: 'translateY(-6px) scale(1.02)',
                boxShadow: '0 12px 40px rgba(245,158,11,0.6), 0 0 0 2px rgba(251,191,36,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
                border: '2px solid rgba(251,191,36,0.8)',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
              },
              '&::before': {
                content: '"// Data & ML Pipeline"',
                position: 'absolute',
                top: 8,
                left: 12,
                fontSize: '9px',
                fontFamily: '"Fira Code", monospace',
                color: 'rgba(0,0,0,0.3)',
                letterSpacing: '0.5px',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(196,181,253,0.3), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover::after': {
                left: '100%',
              }
            }}
          >
            Data & ML Engineer
          </Button>
        </Box>
      </Box>

      {/* Our Culture Section */}
      <Box sx={{ mt: 'auto', mb: 1, py: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{
            mb: 3,
            fontWeight: 700,
            background: 'linear-gradient(90deg, #67e8f9 0%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Our Cultural Values
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 4, sm: 6, md: 8 },
            justifyContent: 'space-evenly',
            flexWrap: 'wrap',
            px: 2,
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          {[
            {
              icon: LightbulbOutlined,
              label: 'Think Outside the Box',
            },
            {
              icon: GroupsOutlined,
              label: 'Bet on the Young Talent',
            },
            {
              icon: RocketLaunchOutlined,
              label: 'Think Big',
            },
            {
              icon: AutoAwesomeOutlined,
              label: 'Deliver in Style',
            },
          ].map((culture, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: 'rgba(245,158,11,0.1)',
                  border: '2px solid rgba(245,158,11,0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(245,158,11,0.2)',
                    borderColor: 'rgba(245,158,11,0.5)',
                    boxShadow: '0 4px 16px rgba(245,158,11,0.3)',
                  },
                }}
              >
                <culture.icon
                  titleAccess={culture.label}
                  sx={{
                    width: 50,
                    height: 50,
                    color: '#f59e0b',
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.8rem',
                  textAlign: 'center',
                  maxWidth: 100,
                  lineHeight: 1.3,
                }}
              >
                {culture.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomePage;
